class TrainingReserveCollection extends HTMLElement {
  connectedCallback() {
    this.setupFinder();
    this.setupGuideFilters();
    this.setupCardGalleries();

    this.anchorLinks = Array.from(this.querySelectorAll('[data-reserve-anchor]'));
    this.anchorTargets = this.anchorLinks
      .map((link) => document.querySelector(link.getAttribute('href')))
      .filter(Boolean);

    this.anchorLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (!target) return;

        event.preventDefault();
        const nav = this.querySelector('.reserve-nav');
        const offset = nav ? nav.getBoundingClientRect().height + 16 : 16;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        history.replaceState(null, '', link.getAttribute('href'));
      });
    });

    if ('IntersectionObserver' in window && this.anchorTargets.length) {
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible) return;

          this.anchorLinks.forEach((link) => {
            const active = link.getAttribute('href') === `#${visible.target.id}`;
            link.classList.toggle('is-active', active);
            if (active) {
              link.setAttribute('aria-current', 'true');
              link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            } else {
              link.removeAttribute('aria-current');
            }
          });
        },
        { rootMargin: '-25% 0px -62% 0px', threshold: [0, 0.15, 0.4] }
      );

      this.anchorTargets.forEach((target) => this.sectionObserver.observe(target));
    }
  }

  disconnectedCallback() {
    if (this.sectionObserver) this.sectionObserver.disconnect();
    if (this.cardGalleries) {
      this.cardGalleries.forEach((gallery) => {
        if (gallery.interval) window.clearInterval(gallery.interval);
      });
    }
    if (this.handleReserveFilterPopState) {
      window.removeEventListener('popstate', this.handleReserveFilterPopState);
    }
  }

  setupCardGalleries() {
    const finePointer = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.cardGalleries = Array.from(this.querySelectorAll('[data-reserve-card-gallery]'))
      .map((gallery) => {
        const track = gallery.querySelector('[data-reserve-card-gallery-track]');
        const slides = Array.from(gallery.querySelectorAll('[data-reserve-card-gallery-slide]'));
        const dots = Array.from(gallery.querySelectorAll('[data-reserve-card-gallery-dot]'));
        const current = gallery.querySelector('[data-reserve-card-gallery-current]');
        if (!track || slides.length < 2) return null;

        const state = { gallery, track, slides, dots, current, index: 0, interval: null, scrollFrame: null };

        const setActive = (index, { scroll = false } = {}) => {
          const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
          state.index = nextIndex;
          slides.forEach((slide, slideIndex) => {
            const active = slideIndex === nextIndex;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', String(!active));
          });
          dots.forEach((dot, dotIndex) => {
            const active = dotIndex === nextIndex;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-pressed', String(active));
          });
          if (current) current.textContent = String(nextIndex + 1);
          if (scroll) {
            track.scrollTo({
              left: track.clientWidth * nextIndex,
              behavior: reducedMotion.matches ? 'auto' : 'smooth'
            });
          }
        };

        const stopPreview = () => {
          if (!state.interval) return;
          window.clearInterval(state.interval);
          state.interval = null;
        };

        const startPreview = () => {
          if (!finePointer.matches || reducedMotion.matches || state.interval) return;
          state.interval = window.setInterval(() => {
            setActive((state.index + 1) % slides.length);
          }, 1150);
        };

        gallery.addEventListener('mouseenter', startPreview);
        gallery.addEventListener('mouseleave', stopPreview);
        gallery.addEventListener('focusout', (event) => {
          if (!gallery.contains(event.relatedTarget)) stopPreview();
        });

        dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            stopPreview();
            setActive(Number(dot.dataset.galleryIndex || 0), { scroll: !finePointer.matches });
          });
        });

        track.addEventListener(
          'scroll',
          () => {
            if (finePointer.matches || state.scrollFrame) return;
            state.scrollFrame = window.requestAnimationFrame(() => {
              const width = track.clientWidth || 1;
              setActive(Math.round(track.scrollLeft / width));
              state.scrollFrame = null;
            });
          },
          { passive: true }
        );

        setActive(0);
        return state;
      })
      .filter(Boolean);
  }

  setupFinder() {
    this.finder = this.querySelector('[data-reserve-finder]');
    if (!this.finder) return;

    this.caliberButtons = Array.from(this.finder.querySelectorAll('[data-reserve-caliber]'));
    this.intentButtons = Array.from(this.finder.querySelectorAll('[data-reserve-intent]'));
    this.recommendations = Array.from(this.finder.querySelectorAll('[data-reserve-recommendation]'));
    this.finderSummary = this.finder.querySelector('[data-reserve-finder-summary]');
    this.finderMatches = this.finder.querySelector('[data-reserve-finder-matches]');

    this.caliberButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const isPressed = button.getAttribute('aria-pressed') === 'true';
        button.setAttribute('aria-pressed', String(!isPressed));
        this.renderFinder();
      });
    });

    this.intentButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.intentButtons.forEach((intentButton) => {
          intentButton.setAttribute('aria-pressed', String(intentButton === button));
        });
        this.renderFinder();
      });
    });

    this.renderFinder();
  }

  setupGuideFilters() {
    this.filterPanel = this.querySelector('[data-reserve-filter-status]');
    this.filterLabel = this.querySelector('[data-reserve-filter-label]');
    this.filterClear = this.querySelector('[data-reserve-filter-clear]');
    this.filterCards = this.querySelector('[data-reserve-filter-cards]');
    this.filterTriggers = Array.from(this.querySelectorAll('[data-reserve-fit-trigger]'));
    this.offerCards = Array.from(this.querySelectorAll('[data-reserve-offer-card]'));

    if (!this.filterPanel || !this.filterCards || !this.filterTriggers.length || !this.offerCards.length) return;

    this.offerCardOrigins = this.offerCards.map((card) => {
      const placeholder = document.createComment('reserve-card-origin');
      card.parentNode.insertBefore(placeholder, card);
      return { card, placeholder };
    });

    this.filterTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.applyOfferFilter(trigger.dataset.reserveFitTrigger, trigger.dataset.reserveFitLabel);
      });
    });

    if (this.filterClear) {
      this.filterClear.addEventListener('click', () => this.clearOfferFilter());
    }

    this.handleReserveFilterPopState = () => {
      const fit = new URL(window.location.href).searchParams.get('reserve_fit');
      const trigger = this.filterTriggers.find((item) => item.dataset.reserveFitTrigger === fit);
      if (trigger) {
        this.applyOfferFilter(fit, trigger.dataset.reserveFitLabel, { updateHistory: false });
      } else {
        this.clearOfferFilter({ updateHistory: false });
      }
    };
    window.addEventListener('popstate', this.handleReserveFilterPopState);

    const initialFit = new URL(window.location.href).searchParams.get('reserve_fit');
    const initialTrigger = this.filterTriggers.find((item) => item.dataset.reserveFitTrigger === initialFit);
    if (initialTrigger) {
      this.applyOfferFilter(initialFit, initialTrigger.dataset.reserveFitLabel, {
        updateHistory: false,
        scroll: false
      });
    }
  }

  applyOfferFilter(fit, label, { updateHistory = true, scroll = true } = {}) {
    const matchingCards = this.offerCards.filter((card) =>
      (card.dataset.reserveFit || '')
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean)
        .includes(fit)
    );
    if (!matchingCards.length) return;

    this.restoreOfferCards();
    this.setAttribute('data-reserve-filtered', 'true');
    this.offerCards.forEach((card) => {
      card.hidden = !matchingCards.includes(card);
    });
    matchingCards.forEach((card) => {
      card.hidden = false;
      this.filterCards.appendChild(card);
    });

    if (this.filterLabel) this.filterLabel.textContent = label || 'your training goal';
    this.filterPanel.hidden = false;

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.set('reserve_fit', fit);
      url.hash = 'reserve-results';
      history.pushState({ reserveFit: fit }, '', url);
    }

    if (scroll) {
      requestAnimationFrame(() => {
        const top = this.filterPanel.getBoundingClientRect().top + window.scrollY - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }
  }

  clearOfferFilter({ updateHistory = true, scroll = true } = {}) {
    if (!this.filterPanel || !this.offerCards?.length) return;

    this.restoreOfferCards();
    this.removeAttribute('data-reserve-filtered');
    this.offerCards.forEach((card) => {
      card.hidden = false;
    });
    this.filterPanel.hidden = true;

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.searchParams.delete('reserve_fit');
      url.hash = 'reserve-starter';
      history.pushState(null, '', url);
    }

    if (scroll) {
      requestAnimationFrame(() => {
        const target = this.querySelector('#reserve-starter');
        if (!target) return;
        const nav = this.querySelector('.reserve-nav');
        const offset = nav ? nav.getBoundingClientRect().height + 16 : 16;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }
  }

  restoreOfferCards() {
    if (!this.offerCardOrigins?.length) return;
    this.offerCardOrigins.forEach(({ card, placeholder }) => {
      if (!placeholder.parentNode) return;
      placeholder.parentNode.insertBefore(card, placeholder.nextSibling);
    });
  }

  renderFinder() {
    const selectedCalibers = this.caliberButtons
      .filter((button) => button.getAttribute('aria-pressed') === 'true')
      .map((button) => button.dataset.reserveCaliber);
    const selectedIntent =
      this.intentButtons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.reserveIntent || 'all';

    this.recommendations.forEach((recommendation) => {
      recommendation.hidden = true;
      recommendation.removeAttribute('data-reserve-match');
      recommendation.removeAttribute('data-reserve-rank');
      const matchLabel = recommendation.querySelector('[data-reserve-match-label]');
      if (matchLabel) matchLabel.hidden = true;
    });

    if (!selectedCalibers.length) {
      this.finderSummary.textContent = 'Select at least one caliber to see the stockpiles built around it.';
      return;
    }

    const prioritizeCaliberCoverage = selectedCalibers.length >= 3;
    const scored = this.recommendations
      .map((recommendation, index) => {
        const offerCalibers = (recommendation.dataset.reserveCalibers || '')
          .split(',')
          .map((caliber) => caliber.trim())
          .filter(Boolean);
        const offerFits = (recommendation.dataset.reserveFits || '')
          .split(',')
          .map((fit) => fit.trim())
          .filter(Boolean);
        const starterFit = selectedIntent === 'starter' && offerFits.includes('fundamentals');
        const groupMatches =
          prioritizeCaliberCoverage ||
          selectedIntent === 'all' ||
          recommendation.dataset.reserveGroup === selectedIntent ||
          offerFits.includes(selectedIntent) ||
          starterFit;
        const matchingCount = selectedCalibers.filter((caliber) => offerCalibers.includes(caliber)).length;
        const exactMatch = selectedCalibers.every((caliber) => offerCalibers.includes(caliber));
        return { recommendation, offerCalibers, groupMatches, matchingCount, exactMatch, index };
      })
      .filter((item) => item.groupMatches && item.matchingCount > 0);

    const exactMatches = scored.filter((item) => item.exactMatch);
    const candidates = exactMatches.length ? exactMatches : scored;
    candidates.sort((a, b) => {
      if (b.matchingCount !== a.matchingCount) return b.matchingCount - a.matchingCount;
      const aExtra = a.offerCalibers.length - a.matchingCount;
      const bExtra = b.offerCalibers.length - b.matchingCount;
      if (aExtra !== bExtra) return aExtra - bExtra;
      return a.index - b.index;
    });

    const visibleMatches = candidates.slice(0, 4);
    visibleMatches.forEach(({ recommendation }, index) => {
      if (this.finderMatches) this.finderMatches.appendChild(recommendation);
      recommendation.hidden = false;
      recommendation.setAttribute('data-reserve-match', 'true');
      recommendation.setAttribute('data-reserve-rank', String(index + 1));
      const matchLabel = recommendation.querySelector('[data-reserve-match-label]');
      if (matchLabel && index === 0) matchLabel.hidden = false;
    });

    if (!visibleMatches.length) {
      this.finderSummary.textContent =
        'No current stockpile matches that combination and depth. Try “Show all matches” or select another training goal.';
    } else if (exactMatches.length) {
      this.finderSummary.textContent = `${visibleMatches.length} stockpile${visibleMatches.length === 1 ? '' : 's'} ${
        visibleMatches.length === 1 ? 'matches' : 'match'
      } your calibers.`;
    } else {
      this.finderSummary.textContent =
        'Your best match covers the largest part of your caliber selection. Additional options follow.';
    }
  }
}

if (!customElements.get('training-reserve-collection')) {
  customElements.define('training-reserve-collection', TrainingReserveCollection);
}
